"use client";

import * as React from "react";
import { Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface TimePickerProps {
  value?: string;
  onChange?: (time: string | undefined) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  filterTime?: (time: string) => boolean;
}

export function TimePicker({
  value,
  onChange,
  className,
  placeholder = "--:--",
  disabled,
  filterTime,
}: TimePickerProps) {
  // Genera opzioni orarie a intervalli di 15 minuti (00:00 -> 23:45)
  const timeOptions = React.useMemo(() => {
    let options: string[] = [];
    for (let h = 0; h < 24; h++) {
      const hourStr = String(h).padStart(2, "0");
      for (let m = 0; m < 60; m += 15) {
        const minStr = String(m).padStart(2, "0");
        options.push(`${hourStr}:${minStr}`);
      }
    }
    if (filterTime) {
      options = options.filter(filterTime);
    }
    if (value && !options.includes(value)) {
      options.push(value);
      options.sort();
    }
    return options;
  }, [value, filterTime]);

  return (
    <Select
      value={value || ""}
      onValueChange={(val) => onChange?.(val || undefined)}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(
          "h-8 text-xs flex items-center justify-between font-normal px-2.5",
          !value && "text-muted-foreground",
          className,
        )}
      >
        <div className="flex items-center gap-1.5 truncate">
          <Clock className="h-3.5 w-3.5 shrink-0 opacity-50" />
          <SelectValue placeholder={placeholder}>
            {value || placeholder}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-48 min-w-[7rem]">
        {timeOptions.map((time) => (
          <SelectItem key={time} value={time} className="text-xs font-mono">
            {time}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
